// OP-3605 — Nova solicitação (ESL2). Equipamento é pedido para atender UM
// produtor em um ou mais eventos; as datas dos eventos decidem por quanto
// tempo o equipamento fica comprometido. O servidor resolve nomes e valida a
// regra; aqui só o shape e a experiência de preencher.
import { FC, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  useEstoqueClientes, useEstoqueEventosDoCliente, useEstoqueItensSolicitacao, useEstoqueLocais, useEstoqueTecnicos,
} from '../../../../hooks/estoque'
import { createEstoqueSolicitacao } from '../../../../Service/estoque'
import { ROTULO_CAMPO, TIPO_DEMANDA_LABEL, TipoDemanda, UFS_BR } from '../../../../types/estoque'
import {
  BD, Button, Card, Field, Input, PageSubtitle, PageTitle, Select, SoCelular, SoDesktop, Spinner,
} from '../../styles'
import { fmtDate } from '../../statusUi'
import { useCelular } from '../../useCelular'
import { BarraFixa } from '../BarraFixa'
import { tratarErro } from '../erros'
import { mascaraDocumento } from '../documento'
import {
  CAMPOS_COM_DESPACHO, CAMPOS_DO_CEP, DIAS_UTEIS_DESPACHO, EstadoForm, FORM_INICIAL, PASSOS, ULTIMO_PASSO, antesDoDespacho, camposDoFormulario, camposDoTipo, camposOpcionaisDoTipo, despachoAte, digitos, enderecoDoCep, hojeISO, impedimentoDoPasso, itemServeAoTipo, mascaraCep, mascaraTelefone, montarPayload, quantidadesDoTipo, tamanhoMaximoDoCampo, validarFormulario,
} from './form'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const TIPOS: TipoDemanda[] = ['ENVIO', 'INSTALACAO', 'PORTARIA']

const Erro: FC<{ msg?: string }> = ({ msg }) => (msg ? <div style={{ color: BD.red, fontSize: 12, marginTop: 4 }}>{msg}</div> : null)

/** Uma linha da conferência: rótulo à esquerda, o que foi escolhido à direita. */
const Resumo: FC<{ rotulo: string; valor: string }> = ({ rotulo, valor }) => (
  <div style={{ display: 'flex', gap: 12, padding: '8px 0', borderTop: `1px solid ${BD.grayBorder}`, fontSize: 13 }}>
    <span style={{ flexShrink: 0, minWidth: 96, color: BD.grayText }}>{rotulo}</span>
    <span style={{ flexGrow: 1 }}>{valor}</span>
  </div>
)

export const EstoqueSolicitar: FC = () => {
  const navigate = useNavigate()
  const hoje = useMemo(hojeISO, [])
  const despacho = useMemo(() => despachoAte(hoje), [hoje])
  const [f, setF] = useState<EstadoForm>(FORM_INICIAL)
  const [erros, setErros] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [buscaCliente, setBuscaCliente] = useState('')
  const [buscaTecnico, setBuscaTecnico] = useState('')
  const [clienteNome, setClienteNome] = useState<string | null>(null)
  const [tecnicoNome, setTecnicoNome] = useState<string | null>(null)
  const [cepBuscando, setCepBuscando] = useState(false)
  const [cepErro, setCepErro] = useState<string | null>(null)

  const { data: clientes, isLoading: clientesCarregando, isError: clientesFalharam, isUnauthorized, mutate: recarregarClientes, termo: termoCliente } = useEstoqueClientes(buscaCliente)
  const buscandoCliente = buscaCliente.trim() !== termoCliente
  const { data: eventos, isLoading: eventosCarregando } = useEstoqueEventosDoCliente(f.clienteId)
  const { data: tecnicos, termo: termoTecnico } = useEstoqueTecnicos(buscaTecnico)
  const buscandoTecnico = buscaTecnico.trim() !== termoTecnico
  const { locais } = useEstoqueLocais()
  const { data: itens, isLoading: itensCarregando } = useEstoqueItensSolicitacao()

  const campos = camposDoTipo(f.tipoDemanda)
  const camposForm = camposDoFormulario(f.tipoDemanda)
  const opcionais = camposOpcionaisDoTipo(f.tipoDemanda)
  const itensDoTipo = useMemo(() => itens.filter((i) => itemServeAoTipo(i.tiposDemanda, f.tipoDemanda)), [itens, f.tipoDemanda])
  const idsVisiveis = useMemo(() => itensDoTipo.map((i) => i.id), [itensDoTipo])
  const [itensRemovidos, setItensRemovidos] = useState<string[]>([])
  const limparErros = (...chaves: string[]) => setErros((e) => {
    if (!chaves.some((k) => k in e)) return e
    const proximo = { ...e }
    chaves.forEach((k) => { delete proximo[k] })
    return proximo
  })
  const set = (patch: Partial<EstadoForm>) => {
    setF((s) => ({ ...s, ...patch }))
    limparErros(...Object.keys(patch).map((k) => (k === 'quantidades' ? 'itens' : k)))
  }
  const setCampo = (c: string, v: string) => {
    setF((s) => ({ ...s, campos: { ...s.campos, [c]: v } }))
    limparErros(c)
  }
  const celular = useCelular()
  const [passo, setPasso] = useState(1)
  const impPasso = impedimentoDoPasso(passo, f, hoje, idsVisiveis)
  const mostra = (n: number) => !celular || passo === n
  const avancar = () => setPasso((p) => Math.min(ULTIMO_PASSO, p + 1))
  const voltar = () => setPasso((p) => Math.max(1, p - 1))

  const impedimento = useMemo(() => {
    const e = validarFormulario(f, hoje, idsVisiveis)
    const primeira = Object.keys(e)[0]
    return primeira ? e[primeira] : null
  }, [f, hoje, idsVisiveis])

  /**
   * O endereço vem do CEP — ninguém digita rua, cidade e estado.
   *
   * Mesmo serviço já usado no cadastro de técnicos (ViaCEP). A busca dispara
   * assim que os 8 dígitos estão completos; o campo Número continua sendo do
   * operador, porque o CEP não o conhece.
   */
  const buscarCep = async (valor: string) => {
    const d = digitos(valor)
    if (d.length !== 8) return
    setCepBuscando(true); setCepErro(null)
    try {
      const res = await fetch(`https://viacep.com.br/ws/${d}/json/`)
      const achado = enderecoDoCep(await res.json())
      if (!achado) {
        setCepErro('CEP não encontrado. Confira o número.')
        setF((st) => ({ ...st, campos: { ...st.campos, endereco: '', cidade: '', uf: '' } }))
        return
      }
      setF((st) => ({ ...st, campos: { ...st.campos, endereco: achado.endereco, cidade: achado.cidade, uf: achado.uf } }))
      limparErros('endereco', 'cidade', 'uf')
      if (!achado.endereco) setCepErro('Este CEP não tem rua própria: informe a rua no campo Número, junto do número.')
    } catch {
      setCepErro('Não foi possível consultar o CEP agora. Tente de novo.')
    } finally {
      setCepBuscando(false)
    }
  }

  const escolherCliente = (id: number, nome: string) => {
    setClienteNome(nome)
    set({ clienteId: id, eventos: [] })
  }
  const alternarEvento = (id: number) =>
    set({ eventos: f.eventos.indexOf(id) >= 0 ? f.eventos.filter((e) => e !== id) : [...f.eventos, id] })

  const trocarTipo = (tipo: TipoDemanda) => {
    const { quantidades, removidos } = quantidadesDoTipo(f.quantidades, itens, tipo)
    set({ tipoDemanda: tipo, tecnicoId: null, quantidades })
    setItensRemovidos(removidos.map((id) => itens.find((i) => i.id === id)?.nome).filter((n): n is string => !!n))
  }

  const enviar = async () => {
    const e = validarFormulario(f, hoje, idsVisiveis)
    setErros(e)
    if (Object.keys(e).length) return
    setBusy(true)
    try {
      const r = await createEstoqueSolicitacao(montarPayload(f, idsVisiveis))
      const numero = r?.data?.data?.numero
      toast.success(numero ? `Solicitação ${numero} enviada` : 'Solicitação enviada')
      navigate('/interno/estoque/minhas')
    } catch (err) {
      const t = tratarErro(err)
      if (t.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      // Erro de campo do servidor volta para o campo; o resto vira aviso.
      const m = /^CAMPO_OBRIGATORIO_(.+)$/.exec(t.codigo)
      if (m) {
        const chave = campos.find((c) => c.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase() === m[1])
        if (chave) setErros({ [chave]: t.texto })
      }
      toast.error(t.texto)
    } finally {
      setBusy(false)
    }
  }

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />

  const ultimoEvento = eventos.filter((e) => f.eventos.indexOf(e.id) >= 0).map((e) => e.data).filter(Boolean).sort().pop()

  return (
    <>
      <PageTitle>Nova solicitação</PageTitle>
      <SoDesktop>
        <PageSubtitle>Equipamento é solicitado para atender um produtor em um ou mais eventos. Conferir disponibilidade, escolher as unidades e reservar é da Logística.</PageSubtitle>
      </SoDesktop>

      <SoCelular>
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {passo > 1 && (
              <Button $variant="secondary" onClick={voltar} aria-label="Passo anterior">‹</Button>
            )}
            <div style={{ flexGrow: 1 }}>
              <div style={{ fontWeight: 700 }}>{PASSOS[passo - 1].titulo}</div>
              <div style={{ fontSize: 12, color: BD.grayText }}>Passo {passo} de {ULTIMO_PASSO}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4, marginTop: 10 }}>
            {PASSOS.map((p) => (
              <span key={p.numero} style={{ flex: '1 1 0', height: 4, borderRadius: 2, background: p.numero <= passo ? BD.red : BD.grayBorder }} />
            ))}
          </div>
        </div>
      </SoCelular>

      {mostra(1) && (
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>1 · Produtor</div>
        {f.clienteId ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontWeight: 600 }}>{clienteNome}</div>
            <Button $variant="secondary" onClick={() => { set({ clienteId: null, eventos: [] }); setClienteNome(null) }}>Trocar produtor</Button>
          </div>
        ) : (
          <>
            <Field>
              <label>Produtor</label>
              <Input value={buscaCliente} placeholder="digite para filtrar" onChange={(e) => setBuscaCliente(e.target.value)} />
            </Field>
            <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 8 }}>
              {(clientesCarregando || buscandoCliente) && <Spinner />}
              {!clientesCarregando && !buscandoCliente && clientesFalharam && (
                <div style={{ color: BD.red, fontSize: 13 }}>
                  Não foi possível carregar os produtores. <Button $variant="secondary" onClick={() => recarregarClientes()}>Tentar de novo</Button>
                </div>
              )}
              {!clientesCarregando && !buscandoCliente && !clientesFalharam && clientes.length === 0 && (
                <div style={{ color: BD.grayText, fontSize: 13 }}>
                  {termoCliente
                    ? <>Nenhum produtor com evento apto para “{termoCliente}”. Confira a grafia, ou se os eventos dele estão no painel.</>
                    : 'Nenhum produtor com evento apto. O cadastro e os eventos são no painel.'}
                </div>
              )}
              {!buscandoCliente && clientes.map((c) => (
                <div key={c.id} onClick={() => escolherCliente(c.id, c.nome)} style={{ padding: '6px 4px', cursor: 'pointer', borderBottom: `1px solid ${BD.grayBorder}` }}>
                  {c.nome} <span style={{ color: BD.grayText, fontSize: 12 }}>· {c.eventosAptos} evento{c.eventosAptos === 1 ? '' : 's'} apto{c.eventosAptos === 1 ? '' : 's'}</span>
                </div>
              ))}
            </div>
            <Erro msg={erros.clienteId} />
          </>
        )}
      </Card>
      )}

      {mostra(1) && f.clienteId && (
        <Card style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>2 · Eventos <span style={{ color: BD.grayText, fontWeight: 400 }}>({f.eventos.length} selecionado{f.eventos.length === 1 ? '' : 's'})</span></div>
          {eventosCarregando && <Spinner />}
          {!eventosCarregando && eventos.length === 0 && (
            <div style={{ color: BD.red, fontSize: 13 }}>Este produtor não tem evento apto no painel. Sem evento não há como saber quando o equipamento volta ao estoque.</div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {eventos.map((e) => {
              const on = f.eventos.indexOf(e.id) >= 0
              return (
                <Button key={e.id} $variant={on ? 'primary' : 'secondary'} onClick={() => alternarEvento(e.id)}>
                  {on ? '✓ ' : ''}{e.nome} <span style={{ opacity: 0.7 }}>{fmtDate(e.data)}</span>
                </Button>
              )
            })}
          </div>
          {ultimoEvento && (
            <div style={{ fontSize: 13, color: BD.grayText, marginTop: 8 }}>Último evento em {fmtDate(ultimoEvento)}: o equipamento fica comprometido até depois dele.</div>
          )}
          <Erro msg={erros.eventos} />
        </Card>
      )}

      {mostra(2) && f.clienteId && (
        <Card style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>3 · Tipo de demanda</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <Field>
              <label>O que você precisa</label>
              <Select value={f.tipoDemanda} onChange={(e) => trocarTipo(e.target.value as TipoDemanda)}>
                {TIPOS.map((t) => <option key={t} value={t}>{TIPO_DEMANDA_LABEL[t]}</option>)}
              </Select>
            </Field>
            <Field>
              <label>Praça de onde sai</label>
              <Select value={f.localOrigemId ?? ''} onChange={(e) => set({ localOrigemId: Number(e.target.value) || null })}>
                <option value="">— escolha —</option>
                {/* Só praças do escopo: o servidor recusa as demais (LOCAL_ORIGEM_INVALIDO) */}
                {locais.filter((l) => l.noEscopo).map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
              </Select>
              <Erro msg={erros.localOrigemId} />
            </Field>
            {f.tipoDemanda === 'PORTARIA' && (
              <Field>
                <label>Técnico responsável</label>
                {f.tecnicoId ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span>{tecnicoNome}</span>
                    <Button $variant="secondary" onClick={() => { set({ tecnicoId: null }); setTecnicoNome(null) }}>Trocar</Button>
                  </div>
                ) : (
                  <>
                    <Input value={buscaTecnico} placeholder="nome (2+ letras)" onChange={(e) => setBuscaTecnico(e.target.value)} />
                    {!buscandoTecnico && tecnicos.map((t) => (
                      <div key={t.id} onClick={() => { set({ tecnicoId: t.id }); setTecnicoNome(t.nome) }} style={{ padding: '4px 2px', cursor: 'pointer' }}>{t.nome}</div>
                    ))}
                  </>
                )}
                <Erro msg={erros.tecnicoId} />
              </Field>
            )}
          </div>
          <div style={{ fontSize: 13, color: BD.grayText, marginTop: 8 }}>
            {f.tipoDemanda === 'PORTARIA'
              ? 'Atendimento de portaria: o equipamento vai com uma pessoa, não para um endereço. Por isso não pede endereço nem dados de envio.'
              : 'Quem responde pelo equipamento é o produtor.'}
          </div>
          {camposForm.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 12 }}>
              {camposForm.map((c) => (
                <Field key={c} style={c === 'pontoReferencia' ? { gridColumn: '1 / -1' } : undefined}>
                  <label>{ROTULO_CAMPO[c] ?? c}{opcionais.indexOf(c) >= 0 ? ' (opcional)' : ''}</label>
                  {c === 'uf' ? (
                    // Vem do CEP como os demais; fica desabilitado para não
                    // abrir a porta de um estado que contradiz o CEP digitado.
                    <Select value={f.campos.uf ?? ''} disabled onChange={(e) => setCampo('uf', e.target.value)}>
                      <option value="">— UF —</option>
                      {UFS_BR.map((u) => <option key={u} value={u}>{u}</option>)}
                    </Select>
                  ) : (
                    <Input
                      type={/^(prazoEntrega|dataInstalacao|dataAtendimento|janelaInicio)$/.test(c) ? 'date' : c === 'contatoTelefone' ? 'tel' : 'text'}
                      min={/^(prazoEntrega|dataInstalacao|dataAtendimento|janelaInicio)$/.test(c) ? hoje : undefined}
                      maxLength={tamanhoMaximoDoCampo(c)}
                      placeholder={c === 'cep' ? '00000-000' : c === 'contatoTelefone' ? '(00) 00000-0000' : c === 'numero' ? '123 ou S/N' : c === 'contatoDocumento' ? 'CPF ou CNPJ de quem vai receber' : c === 'complemento' ? 'apto, bloco, sala' : c === 'pontoReferencia' ? 'ex.: portão lateral, em frente à padaria' : undefined}
                      // Rua, cidade e estado vêm do CEP: digitar de novo é
                      // convite a divergir do que o CEP diz.
                      readOnly={CAMPOS_DO_CEP.indexOf(c) >= 0}
                      style={CAMPOS_DO_CEP.indexOf(c) >= 0 ? { background: BD.grayBg, cursor: 'not-allowed' } : undefined}
                      value={f.campos[c] ?? ''}
                      onChange={(e) => {
                        if (CAMPOS_DO_CEP.indexOf(c) >= 0) return
                        if (c === 'cep') {
                          const v = mascaraCep(e.target.value)
                          setCampo('cep', v)
                          setCepErro(null)
                          if (digitos(v).length === 8) buscarCep(v)
                          return
                        }
                        if (c === 'contatoTelefone') { setCampo(c, mascaraTelefone(e.target.value)); return }
                        if (c === 'contatoDocumento') { setCampo(c, mascaraDocumento(e.target.value)); return }
                        setCampo(c, e.target.value)
                      }}
                    />
                  )}
                  {c === 'cep' && cepBuscando && <div style={{ fontSize: 12, color: BD.grayText, marginTop: 4 }}>buscando o endereço…</div>}
                  {c === 'cep' && cepErro && <div style={{ fontSize: 12, color: BD.red, marginTop: 4 }}>{cepErro}</div>}
                  {CAMPOS_DO_CEP.indexOf(c) >= 0 && !f.campos[c] && !cepBuscando && (
                    <div style={{ fontSize: 12, color: BD.grayText, marginTop: 4 }}>preenchido pelo CEP</div>
                  )}
                  {CAMPOS_COM_DESPACHO.indexOf(c) >= 0 && (
                    <div style={{ fontSize: 12, color: BD.grayText, marginTop: 4 }}>
                      A Logística tem até {DIAS_UTEIS_DESPACHO} dias úteis após a solicitação para despachar (até {fmtDate(despacho)}). Conte também o tempo de transporte.
                    </div>
                  )}
                  {antesDoDespacho(c, f.campos[c], hoje, despacho) && (
                    <div style={{ fontSize: 12, color: BD.redDark, marginTop: 4 }}>Esta data não cobre o prazo de despacho e o transporte, e pode não ser cumprida. Combine com a Logística.</div>
                  )}
                  <Erro msg={erros[c]} />
                </Field>
              ))}
            </div>
          )}
        </Card>
      )}

      {mostra(3) && f.clienteId && (
        <Card style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>4 · Equipamentos</div>
          {itensCarregando && <Spinner />}
          {itensRemovidos.length > 0 && (
            <div role="status" style={{ fontSize: 13, color: BD.grayText, marginBottom: 8 }}>
              {itensRemovidos.join(', ')} {itensRemovidos.length > 1 ? 'saíram' : 'saiu'} do pedido: não {itensRemovidos.length > 1 ? 'servem' : 'serve'} a {TIPO_DEMANDA_LABEL[f.tipoDemanda] ?? f.tipoDemanda}.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            {itensDoTipo.map((i) => (
              <Field key={i.id}>
                <label>{i.nome}{i.unidadeMedida ? ` (${i.unidadeMedida})` : ''}</label>
                <Input type="number" min={0} step={1} value={f.quantidades[i.id] ?? ''} onChange={(e) => set({ quantidades: { ...f.quantidades, [i.id]: Number(e.target.value) || 0 } })} />
              </Field>
            ))}
          </div>
          <Erro msg={erros.itens} />
          <Field style={{ marginTop: 12 }}>
            <label>Observação</label>
            <Input value={f.observacao} maxLength={1000} onChange={(e) => set({ observacao: e.target.value })} />
          </Field>
          <SoDesktop>
            <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button disabled={busy || !!impedimento} onClick={enviar}>{busy ? 'Enviando…' : 'Enviar solicitação'}</Button>
              {impedimento && <span style={{ fontSize: 13, color: BD.grayText }}>{impedimento}</span>}
            </div>
          </SoDesktop>
        </Card>
      )}

      {celular && passo === ULTIMO_PASSO && (
        <Card style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Confira antes de enviar</div>
          <Resumo rotulo="Produtor" valor={clienteNome ?? '—'} />
          <Resumo rotulo="Eventos" valor={eventos.filter((e) => f.eventos.indexOf(e.id) >= 0).map((e) => e.nome).join(', ') || '—'} />
          <Resumo rotulo="Tipo" valor={TIPO_DEMANDA_LABEL[f.tipoDemanda] ?? f.tipoDemanda} />
          <Resumo rotulo="Sai de" valor={locais.find((l) => l.id === f.localOrigemId)?.nome ?? '—'} />
          <Resumo
            rotulo="Itens"
            valor={itensDoTipo
              .filter((i) => Number(f.quantidades[i.id]) > 0)
              .map((i) => `${f.quantidades[i.id]} × ${i.nome}`)
              .join(' · ') || '—'}
          />
          {camposForm
            .filter((c) => opcionais.indexOf(c) < 0 || String(f.campos[c] ?? '').trim())
            .map((c) => (
              <Resumo key={c} rotulo={ROTULO_CAMPO[c] ?? c} valor={String(f.campos[c] ?? '') || '—'} />
            ))}
          {camposForm.some((c) => antesDoDespacho(c, f.campos[c], hoje, despacho)) && (
            <div style={{ fontSize: 12, color: BD.redDark, marginTop: 8 }}>Esta data não cobre o prazo de despacho e o transporte, e pode não ser cumprida. Combine com a Logística.</div>
          )}
          {f.observacao && <Resumo rotulo="Observação" valor={f.observacao} />}
        </Card>
      )}

      {celular && (
        <>
          <BarraFixa>
            {(passo === ULTIMO_PASSO ? impedimento : impPasso) && (
              <span style={{ fontSize: 12, color: BD.grayText }}>{passo === ULTIMO_PASSO ? impedimento : impPasso}</span>
            )}
            {passo === ULTIMO_PASSO ? (
              <Button disabled={busy || !!impedimento} onClick={enviar}>{busy ? 'Enviando…' : 'Enviar solicitação'}</Button>
            ) : (
              <Button disabled={!!impPasso} onClick={avancar}>Continuar</Button>
            )}
          </BarraFixa>
        </>
      )}
    </>
  )
}

export default EstoqueSolicitar
